import React from "react";
import {
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Link,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import moment from "moment";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  background: `linear-gradient(to left, ${theme.palette.grey[700]}, ${theme.palette.grey[800]})`,
  color: theme.palette.common.white,
  fontWeight: 600,
  padding: theme.spacing(1, 1.5),
  borderBottom: "none",
  fontSize: "0.875rem",
  [theme.breakpoints.up("md")]: {
    fontSize: "1rem",
    padding: theme.spacing(1.5, 2),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  height: theme.spacing(4),
  [theme.breakpoints.down("md")]: {
    height: theme.spacing(5),
  },
}));

const KitchenAvailability = ({ kitchen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const formatDateTime = (utcDateTime, isRecurring) => {
    const localTime = moment.utc(utcDateTime).local();
    return isRecurring
      ? `${localTime.format("ddd, h:mm A")}`
      : localTime.format("MMM D, h:mm A");
  };

  const hasAvailability =
    kitchen?.RateEntries && kitchen.RateEntries.length > 0;
  const kitchenName = kitchen?.Title || "this kitchen";

  return (
    <Box
      sx={{
        p: { xs: 0, sm: 0, md: 0 },
        bgcolor: "background.paper",
        borderRadius: 2,
      }}
    >
      <Typography
        variant={isMobile ? "subtitle1" : "h6"}
        color="text.primary"
        fontWeight={600}
        gutterBottom
        sx={{ mb: 1.5 }}
      >
        Rates and Availability
      </Typography>
      <Divider
        sx={{
          mt: 0,
          mb: 0,
          bgcolor: "divider",
          opacity: 0.3,
          "&": {
            marginTop: "8px !important",
            marginBottom: "16px !important",
          },
        }}
      />
      {hasAvailability ? (
        <Box
          sx={{
            overflowY: isMobile ? "auto" : "visible",
            maxHeight: isMobile ? "60vh" : "none",
          }}
        >
          <Table aria-label="rates and availability">
            <TableHead>
              <TableRow>
                <StyledTableCell>Occurs</StyledTableCell>
                <StyledTableCell align="left">Start</StyledTableCell>
                <StyledTableCell align="left">End</StyledTableCell>
                <StyledTableCell align="right">Price</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kitchen.RateEntries.map((entry, index) => (
                <StyledTableRow key={index}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      border: "none",
                      padding: theme.spacing(1, 1.5),
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                      [theme.breakpoints.up("md")]: { fontSize: "1rem" },
                    }}
                  >
                    {entry.recurring ? "Every week" : "One time"}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      border: "none",
                      padding: theme.spacing(1, 1.5),
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                      [theme.breakpoints.up("md")]: { fontSize: "1rem" },
                    }}
                  >
                    {formatDateTime(entry.startDateTime, entry.recurring)}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      border: "none",
                      padding: theme.spacing(1, 1.5),
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                      [theme.breakpoints.up("md")]: { fontSize: "1rem" },
                    }}
                  >
                    {formatDateTime(entry.endDateTime, entry.recurring)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      border: "none",
                      padding: theme.spacing(1, 1.5),
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                      [theme.breakpoints.up("md")]: { fontSize: "1rem" },
                    }}
                  >
                    ${parseFloat(entry.price).toFixed(2)}
                  </TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          The rates and availability for {kitchenName} are not available at the
          moment. Please contact{" "}
          <Link
            href="mailto:info@airkitchenz.com"
            color="primary.main"
            underline="hover"
          >
            info@airkitchenz.com
          </Link>{" "}
          for further inquiries.
        </Typography>
      )}
    </Box>
  );
};

export default KitchenAvailability;
